import { supabase } from './supabase';

export interface Testimonial {
  id: string;
  title: string;
  content: string;
  user_id: string;
  created_at: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  approved_at: string | null;
  approved_by: string | null;
  edit_history: any[];
}

export type TestimonialInput = Pick<Testimonial, 'title' | 'content'>;

/**
 * Busca todos os testemunhos aprovados
 */
export async function fetchApprovedTestimonials(
  options: {
    page?: number;
    perPage?: number;
    searchQuery?: string;
  } = {}
): Promise<{ data: Testimonial[]; count: number }> {
  try {
    const {
      page = 1,
      perPage = 10,
      searchQuery = ''
    } = options;

    // Iniciar a consulta
    let query = supabase
      .from('testimonials')
      .select('*', { count: 'exact' })
      .eq('status', 'aprovado')
      .order('approved_at', { ascending: false });

    // Aplicar busca por palavra-chave
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }

    // Aplicar paginação
    const from = (page - 1) * perPage;
    const to = from + perPage - 1;
    query = query.range(from, to);

    // Executar a consulta
    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  } catch (error) {
    console.error('Error fetching approved testimonials:', error);
    return { data: [], count: 0 };
  }
}

/**
 * Busca todos os testemunhos para o painel admin
 */
export async function fetchAllTestimonials(
  options: {
    status?: 'pendente' | 'aprovado' | 'rejeitado';
    page?: number;
    perPage?: number;
    searchQuery?: string;
  } = {}
): Promise<{ data: Testimonial[]; count: number }> {
  try {
    const {
      status,
      page = 1,
      perPage = 10,
      searchQuery = ''
    } = options;

    // Iniciar a consulta
    let query = supabase
      .from('testimonials')
      .select('*', { count: 'exact' });

    // Aplicar filtro por status
    if (status) {
      query = query.eq('status', status);
    }

    // Aplicar busca por palavra-chave
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
    }

    // Ordenar por data de criação (pendentes primeiro) ou data de aprovação
    if (status === 'pendente') {
      query = query.order('created_at', { ascending: false });
    } else {
      query = query.order('approved_at', { ascending: false });
    }

    // Aplicar paginação
    const from = (page - 1) * perPage;
    const to = from + perPage - 1;
    query = query.range(from, to);

    // Executar a consulta
    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  } catch (error) {
    console.error('Error fetching all testimonials:', error);
    return { data: [], count: 0 };
  }
}

/**
 * Busca os testemunhos do usuário atual
 */
export async function fetchUserTestimonials(): Promise<Testimonial[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return [];

    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user testimonials:', error);
    return [];
  }
}

/**
 * Busca um testemunho pelo ID
 */
export async function getTestimonialById(id: string): Promise<Testimonial | null> {
  try {
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching testimonial:', error);
    return null;
  }
}

/**
 * Cria um novo testemunho
 */
export async function createTestimonial(testimonial: TestimonialInput): Promise<Testimonial | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Usuário não autenticado');

    const { data, error } = await supabase
      .from('testimonials')
      .insert({
        ...testimonial,
        user_id: session.user.id,
        status: 'pendente'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating testimonial:', error);
    return null;
  }
}

/**
 * Atualiza um testemunho existente
 */
export async function updateTestimonial(id: string, testimonial: Partial<TestimonialInput>): Promise<Testimonial | null> {
  try {
    const { data, error } = await supabase
      .from('testimonials')
      .update(testimonial)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating testimonial:', error);
    return null;
  }
}

/**
 * Aprova um testemunho
 */
export async function approveTestimonial(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('testimonials')
      .update({ status: 'aprovado' })
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error approving testimonial:', error);
    return false;
  }
}

/**
 * Rejeita um testemunho
 */
export async function rejectTestimonial(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('testimonials')
      .update({ status: 'rejeitado' })
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error rejecting testimonial:', error);
    return false;
  }
}

/**
 * Exclui um testemunho
 */
export async function deleteTestimonial(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('testimonials')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    return false;
  }
}

/**
 * Verifica se o título está dentro do limite de caracteres
 */
export function isTitleValid(title: string): boolean {
  return title.length > 0 && title.length <= 100;
}

/**
 * Verifica se o conteúdo está dentro do limite de caracteres
 */
export function isContentValid(content: string): boolean {
  return content.length > 0 && content.length <= 2000;
}

/**
 * Retorna o status formatado em português
 */
export function getFormattedStatus(status: string): string {
  switch (status) {
    case 'pendente': return 'Pendente';
    case 'aprovado': return 'Aprovado';
    case 'rejeitado': return 'Rejeitado';
    default: return status;
  }
}